/**
 * A record's timeline as the record page reads it: the first page, then
 * "Show more" for the next, each appended; a search and filters (where the
 * timeline declares them, `ManifestRecord.timeline_search` /
 * `timeline_filters`) narrow it on the server and start again at page 1.
 * Pure: the hook in `hooks.ts` and the page use these.
 */
import type { Paginated, TimelineEvent } from "./contract";

/** Events a page of the timeline holds. */
export const TIMELINE_PAGE_SIZE = 50;

/** What the person typed and picked: the search text and each filter's value. */
export interface TimelineSearch {
  q: string;
  /** Filter param → its value; an empty value is no filter. */
  filters: Record<string, string>;
}

/** One request for a page of the timeline. */
export interface TimelineQuery {
  page: number;
  page_size: number;
  q: string | null;
  filters: Record<string, string>;
}

export function emptyTimelineSearch(): TimelineSearch {
  return { q: "", filters: {} };
}

/** The search as a request for one page: blank text and empty filters are left out. */
export function timelineQuery(
  search: TimelineSearch,
  page: number,
): TimelineQuery {
  const q = search.q.trim();
  return {
    page,
    page_size: TIMELINE_PAGE_SIZE,
    q: q === "" ? null : q,
    filters: Object.fromEntries(
      Object.entries(search.filters).filter(([, value]) => value !== ""),
    ),
  };
}

/** The request's query string, in the order the route reads it. */
export function timelineParams(query: TimelineQuery): URLSearchParams {
  const params = new URLSearchParams({
    page: String(query.page),
    page_size: String(query.page_size),
  });
  if (query.q !== null) params.set("q", query.q);
  for (const [param, value] of Object.entries(query.filters))
    params.append(param, value);
  return params;
}

/** Whether the person has narrowed the timeline (so an empty answer means "nothing matches", not "nothing yet"). */
export function isTimelineNarrowed(search: TimelineSearch): boolean {
  const query = timelineQuery(search, 1);
  return query.q !== null || Object.keys(query.filters).length > 0;
}

/** The events shown so far and how many there are, as pages arrive. */
export interface TimelineShown {
  events: TimelineEvent[];
  total: number;
  /** The last page read, and how many pages there were then. */
  page: number;
  totalPages: number;
}

export function firstTimelinePage(
  answer: Paginated<TimelineEvent>,
): TimelineShown {
  return {
    events: answer.items,
    total: answer.total,
    page: answer.page,
    totalPages: answer.total_pages,
  };
}

/** The next page appended to what is shown; its total wins (events may have arrived since the first page). */
export function withNextTimelinePage(
  shown: TimelineShown,
  answer: Paginated<TimelineEvent>,
): TimelineShown {
  return {
    events: [...shown.events, ...answer.items],
    total: answer.total,
    page: answer.page,
    totalPages: answer.total_pages,
  };
}

/** Whether "Show more" has a page to read. */
export function hasMoreTimeline(shown: TimelineShown): boolean {
  return shown.page < shown.totalPages;
}
